import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { UserIcon, ChevronDownIcon } from '@heroicons/react/24/outline';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';

const Navbar = ({ variant = 'default' }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, isAuthenticated, logout } = useAuth();
  const { isDark } = useTheme();
  const [isDashboardDropdownOpen, setIsDashboardDropdownOpen] = useState(false);

  const isActive = (path) => location.pathname === path;

  const dashboardServices = [
    { name: 'AI Agents', path: '/ai-agents', icon: '🤖' },
    { name: 'Workflows', path: '/workflows', icon: '⚡' },
    { name: 'Analytics', path: '/analytics', icon: '📊' },
    { name: 'API Keys', path: '/api-keys', icon: '🔑' },
    { name: 'Services', path: '/services', icon: '🛍️' },
    { name: 'Settings', path: '/account', icon: '⚙️' }
  ];

  // Different navbar styles for different pages
  const getNavbarStyle = () => {
    const baseStyle = 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-800';
    
    switch (variant) {
      case 'services':
        return `${baseStyle} dark:bg-[#0a1116] dark:border-[#233c48]`;
      case 'transparent':
        return 'bg-white/95 dark:bg-gray-900/95 backdrop-blur-10 border-gray-200/50 dark:border-gray-800/50';
      default:
        return baseStyle;
    }
  };

  const navbarClasses = getNavbarStyle();

  return (
    <header className={`sticky top-0 z-50 border-b transition-all duration-200 ${navbarClasses}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center py-6">
          {/* Logo */}
          <div className="flex items-center">
            <Link to="/" className="flex items-center space-x-3">
              <div className="w-8 h-8 text-primary">
                <svg fill="none" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="24" cy="24" r="20" stroke="currentColor" strokeDasharray="10 4" strokeWidth="4"></circle>
                  <circle cx="24" cy="24" fill="currentColor" r="8"></circle>
                  <path d="M40 24C40 32.8366 32.8366 40 24 40" stroke="currentColor" strokeLinecap="round" strokeWidth="2"></path>
                </svg>
              </div>
              <h2 className="text-xl font-bold text-black dark:text-white hidden sm:block">OrbitMind</h2>
            </Link>
          </div>

          {/* Navigation Links */}
          <nav className="hidden md:flex items-center space-x-8">
            <Link 
              to="/" 
              className={`text-sm font-medium transition-colors hover:text-black dark:hover:text-white ${
                isActive('/') ? 'text-primary font-semibold' : 'text-gray-500 dark:text-gray-300'
              }`}
            >
              Home
            </Link>
            <Link 
              to="/services" 
              className={`text-sm font-medium transition-colors hover:text-black dark:hover:text-white ${
                isActive('/services') || location.pathname.startsWith('/services/') 
                  ? 'text-primary font-semibold' : 'text-gray-500 dark:text-gray-300'
              }`}
            >
              Services
            </Link>
            <Link 
              to="/pricing" 
              className={`text-sm font-medium transition-colors hover:text-black dark:hover:text-white ${
                isActive('/pricing') ? 'text-primary font-semibold' : 'text-gray-500 dark:text-gray-300'
              }`}
            >
              Pricing
            </Link>
            <Link 
              to="/about" 
              className={`text-sm font-medium transition-colors hover:text-black dark:hover:text-white ${
                isActive('/about') ? 'text-primary font-semibold' : 'text-gray-500 dark:text-gray-300'
              }`}
            >
              About
            </Link>
            <Link 
              to="/contact" 
              className={`text-sm font-medium transition-colors hover:text-black dark:hover:text-white ${
                isActive('/contact') ? 'text-primary font-semibold' : 'text-gray-500 dark:text-gray-300'
              }`}
            >
              Contact
            </Link>
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-4">
            {/* Mobile Menu Button */}
            <button className="md:hidden text-gray-500 dark:text-gray-300 hover:text-black dark:hover:text-white">
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              </svg>
            </button>

            {/* Auth Buttons or User Menu */}
            {isAuthenticated ? (
              <div className="flex items-center space-x-4">
                {/* Dashboard Dropdown */}
                <div className="relative">
                  <button 
                    onClick={() => setIsDashboardDropdownOpen(!isDashboardDropdownOpen)}
                    onBlur={() => setTimeout(() => setIsDashboardDropdownOpen(false), 150)}
                    className="flex items-center space-x-1 text-gray-500 dark:text-gray-300 hover:text-black dark:hover:text-white text-sm font-medium transition-colors"
                  >
                    <span>Dashboard</span>
                    <ChevronDownIcon className={`h-4 w-4 transition-transform duration-200 ${isDashboardDropdownOpen ? 'rotate-180' : ''}`} />
                  </button>
                  
                  {/* Dashboard Dropdown Menu */}
                  <div className={`absolute left-0 mt-2 w-56 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 transition-all duration-200 z-50 ${
                    isDashboardDropdownOpen ? 'opacity-100 visible transform translate-y-0' : 'opacity-0 invisible transform -translate-y-2'
                  }`}>
                    <div className="py-2">
                      <div className="px-4 py-2 border-b border-gray-200 dark:border-gray-700">
                        <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider">Quick Access</p>
                      </div>
                      {dashboardServices.map((service, index) => (
                        <Link
                          key={index}
                          to={service.path}
                          onClick={() => setIsDashboardDropdownOpen(false)}
                          className="flex items-center px-4 py-3 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-primary dark:hover:text-primary transition-colors group"
                        >
                          <span className="mr-3 text-lg group-hover:scale-110 transition-transform">{service.icon}</span>
                          <span className="font-medium">{service.name}</span>
                          {service.name === 'AI Agents' && (
                            <span className="ml-auto bg-primary text-white text-xs px-2 py-1 rounded-full">New</span>
                          )}
                        </Link>
                      ))}
                      <div className="border-t border-gray-200 dark:border-gray-700 mt-2 pt-2">
                        <Link
                          to={user?.role === 'ADMIN' || user?.role === 'SUPER_ADMIN' ? '/admin' : '/dashboard'}
                          onClick={() => setIsDashboardDropdownOpen(false)}
                          className="flex items-center px-4 py-3 text-sm text-primary hover:bg-primary/10 transition-colors font-semibold"
                        >
                          <span className="mr-3 text-lg">🏠</span>
                          <span>Main Dashboard</span>
                        </Link>
                      </div>
                    </div>
                  </div>
                </div>
                
                {/* User Profile Dropdown */}
                <div className="relative group">
                  <button className="w-8 h-8 bg-gradient-to-r from-primary to-blue-600 rounded-full flex items-center justify-center hover:from-blue-600 hover:to-primary transition-all duration-300 transform hover:scale-110 shadow-lg">
                    <UserIcon className="h-5 w-5 text-white" />
                  </button>
                  
                  {/* User Dropdown Menu */}
                  <div className="absolute right-0 mt-2 w-48 bg-white dark:bg-gray-800 rounded-lg shadow-lg border border-gray-200 dark:border-gray-700 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-200 z-50">
                    <div className="py-2">
                      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-700">
                        <p className="text-sm font-medium text-black dark:text-white">{user?.username}</p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{user?.email}</p>
                        <span className="inline-block mt-1 px-2 py-1 text-xs bg-primary/10 text-primary rounded-full font-medium">
                          {user?.role}
                        </span>
                      </div>
                      <Link 
                        to="/account" 
                        className="flex items-center px-4 py-2 text-sm text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-black dark:hover:text-white transition-colors"
                      >
                        <span className="mr-3">👤</span>
                        Account Settings
                      </Link>
                      <button 
                        onClick={() => {
                          logout();
                          navigate('/');
                        }}
                        className="flex items-center w-full text-left px-4 py-2 text-sm text-gray-500 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 hover:text-red-500 transition-colors"
                      >
                        <span className="mr-3">🚪</span>
                        Sign Out
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <>
                <Link 
                  to={`/login?returnTo=${encodeURIComponent(location.pathname)}`}
                  className="text-gray-500 dark:text-gray-300 hover:text-black dark:hover:text-white text-sm font-medium transition-colors"
                >
                  Login
                </Link>
                <Link 
                  to="/signup" 
                  className="bg-primary text-white px-4 py-2 rounded-lg hover:bg-blue-600 transition-all duration-200 transform hover:scale-105 text-sm font-medium"
                >
                  Get Started
                </Link>
              </>
            )}
          </div>
        </div>

        {/* Mobile Navigation Menu */}
        <div className="md:hidden border-t border-gray-200 dark:border-gray-800 py-4 hidden" id="mobile-menu">
          <div className="flex flex-col space-y-4">
            <Link 
              to="/" 
              className={`text-sm font-medium transition-colors hover:text-gray-900 dark:hover:text-white ${
                isActive('/') ? 'text-primary font-semibold' : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              Home
            </Link>
            <Link 
              to="/services" 
              className={`text-sm font-medium transition-colors hover:text-gray-900 dark:hover:text-white ${
                isActive('/services') ? 'text-primary font-semibold' : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              Services
            </Link>
            <Link 
              to="/pricing" 
              className={`text-sm font-medium transition-colors hover:text-gray-900 dark:hover:text-white ${
                isActive('/pricing') ? 'text-primary font-semibold' : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              Pricing
            </Link>
            <Link 
              to="/about" 
              className={`text-sm font-medium transition-colors hover:text-gray-900 dark:hover:text-white ${
                isActive('/about') ? 'text-primary font-semibold' : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              About
            </Link>
            <Link 
              to="/contact" 
              className={`text-sm font-medium transition-colors hover:text-gray-900 dark:hover:text-white ${
                isActive('/contact') ? 'text-primary font-semibold' : 'text-gray-600 dark:text-gray-300'
              }`}
            >
              contact
            </Link>
          </div>
        </div>
      </div>
    </header>
  );
};

export default Navbar;